import React, { useState } from "react";
import {
	Space,
	Upload,
	Button,
	Modal,
	Badge,
	notification,
	Row,
	Col,
	Popconfirm,
} from "antd";
import {
	PlusOutlined,
	PaperClipOutlined,
	DeleteOutlined,
} from "@ant-design/icons";
import { newId, getBase64 } from "utils/common";
import { deleteUploadDocument, uploadDocument } from "api";
import _ from "lodash";

const allowedFileTypes = [
	"application/pdf",
	"image/png",
	"image/jpeg",
	"image/gif",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.ms-powerpoint",
	"application/vnd.openxmlformats-officedocument.presentationml.presentation",
	"application/vnd.ms-excel",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"image/bmp",
	"application/octet-stream",
	// "application/vnd.ms-outlook", //disabling outlook since security issues prevent the download
	"message/rfc822",
];

const allowedSize = 10485760 * 2; //20MB

const FileUploader = (props) => {
	const [visible, setVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const {
		attachments,
		attachmentDispatch,
		name,
		dataType,
		isNew,
		questionId,
		isFormNew,
		saveFormDispatch,
		formId,
		disabled,
		...otherProps
	} = props;
	const [attachmentUpserted, fetchAttachmentById] = attachmentDispatch;

	const onPreview = async (file) => {
		try {
			setLoading(true);
			await fetchAttachmentById(file.uid);
			setLoading(false);
		} catch (e) {
			console.log(e);
		}
	};

	const saveForm = async () => {
		return saveFormDispatch();
	};

	const customRequest = async (uploaded) => {
		//first we need to check if the form has been saved
		//if not saved, we need to call the save action first
		setLoading(true);
		if (isFormNew) {
			await saveForm();
		}
		const { file } = uploaded;
		const fileContent = await getBase64(file);
		let attachment = [
			{
				id: newId(),
				responseFormId: formId,
				questionId: name,
				fileName:_.deburr(file.name).replace(/[^0-9a-zA-Z._]/g, ''),
				fileType: file.type,
				fileContent: fileContent,
			},
		];

		try {
			if (attachments.some((a) => a.fileName === file.name)) {
				notification.error({
					message: "Same file uploaded twice",
					description: `You have attached a file with the of '${file.name}' already. Please check that you've selected the correct file`,
					duration: 10,
				});
				return;
			}
			await uploadDocument(attachment);
			uploaded.onSuccess(attachment);
		} catch (error) {
			console.log(error.message);
			uploaded.onError(error, attachment);
		} finally {
			setLoading(false);
		}
	};

	const onSuccess = (attachment) => {
		//dispatch attachment to redux without fileContent
		const { fileContent, ...otherProps } = attachment[0];
		attachmentUpserted({ ...otherProps });
	};

	const onError = (e, attachment) => {
		//todo: global error handler
		console.log(e.message, attachment);
	};

	//shape the fileList prop for antd's Upload component
	//based on our own attachment entity
	let fileList = attachments.map((a) => {
		return {
			uid: a.id,
			name: a.fileName,
			status: "done",
			fileType: a.fileType,
			url: `${process.env.REACT_APP_API_URL}/UploadDocument/GetUploadDocument/${a.id}`,
		};
	});

	const uploadProps = {
		multiple: true,
		action: `${process.env.REACT_APP_API_URL}/api/UploadDocument/UploadDocument`,
		// headers: {
		// 	authorization: authToken,
		// },
		onPreview,
		customRequest,
		onSuccess,
		onError,
	};

	const handleCancel = () => {
		setVisible(false);
	};

	const handleOpenModal = () => {
		setVisible(true);
	};

	const handleDelete = async (f) => {
		//create a postable object, with no file content
		const attachment = {
			id: f.uid,
			responseFormId: formId,
			questionId: name,
			fileName: f.name,
			fileType: f.fileType,
			isDeleted: true,
		};
		try {
			await deleteUploadDocument(attachment);
			//api receives an array of attachments, but redux expects a single object
			attachmentUpserted(attachment);
		} catch (e) {
			console.log(e);
		}
	};

	const getFileType = (file) => {
		if (
			file.name !== null &&
			file.name !== undefined &&
			file.name.endsWith(".msg")
		) {
			return "application/vnd.ms-outlook";
		} else return file.type;
	};

	const beforeUpload = (file) => {
		let hasError = false;
		const fileType = getFileType(file);
		if (!allowedFileTypes.includes(fileType)) {
			notification.error({
				message: "Can't upload this file",
				description: `${file.name} is not supported for uploading. Please check that your attachment is one of the following - image, office document or pdf.`,
				duration: 20,
			});
			hasError = true;
		}
		if (file.size > allowedSize) {
			notification.error({
				message: "Can't upload this file",
				description: `${file.name} is too big (${Number(
					file.size / 1024 / 1024
				).toFixed(
					2
				)} MB). Please check that you are uploading the correct file. If it is an image, please consider cropping it.`,
				duration: 20,
			});
			hasError = true;
		}
		return hasError ? Upload.LIST_IGNORE : true;
	};

	return dataType === "miniAttachment" ? (
		<>
			<Modal
				open={visible}
				title="Attachments"
				footer={[
					<Button key="back" onClick={handleCancel}>
						Close
					</Button>,
				]}
			>
				{fileList.map((f, i) => {
					return (
						<Row key={i}>
							<Col>
								<Button
									key={f.uid}
									type="link"
									onClick={() => onPreview(f)}
								>
									{f.name}
								</Button>
							</Col>
							<Col>
								<Popconfirm
									title="Are you sure you want to delete this file?"
									onConfirm={() => {
										handleDelete(f);
										handleCancel();
									}}
									disabled={disabled}
								>
									<Button
										type="link"
										danger
										disabled={disabled}
									>
										<DeleteOutlined />
									</Button>
								</Popconfirm>
							</Col>
						</Row>
					);
				})}
			</Modal>
			<Space>
				<Upload
					{...uploadProps}
					{...otherProps}
					fileList={fileList}
					showUploadList={false}
					disabled={disabled || loading}
					beforeUpload={beforeUpload}
				>
					{!disabled && (
						<Button
							type="primary"
							{...otherProps}
							icon={<PlusOutlined />}
						/>
					)}
				</Upload>
				{fileList && fileList.length > 0 ? (
					<Badge count={fileList.length}>
						<Button
							onClick={handleOpenModal}
							icon={<PaperClipOutlined />}
						/>
					</Badge>
				) : (
					<> </>
				)}
			</Space>
		</>
	) : (
		<>
			<Upload
				{...uploadProps}
				{...otherProps}
				fileList={fileList}
				disabled={disabled || loading}
				beforeUpload={beforeUpload}
				onRemove={handleDelete}
			>
				{!disabled && (
					<Button
						type="primary"
						{...otherProps}
						icon={<PlusOutlined />}
					>
						Upload
					</Button>
				)}
			</Upload>
		</>
	);
};

export default FileUploader;
